// Services
import api from '../../api'

// Utilities
import CreditCard from '../../utility/creditCardUtil'
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'

import { Cards } from '../../constants'

export default {
  async getPayDebitCardTypes (context) {
    try {
      const cardTypeData = await api.paymentMethod.getDebitCardTypes().catch(e => {
        Logger.debug('Error getting card types, trying again')
        return api.paymentMethod.getDebitCardTypes()
      })

      Logger.info('Got cardTypes')
      Logger.debug(cardTypeData)
      Logger.debug(Object.keys(cardTypeData.cardTypes))

      context.commit('getPayCardTypesSuccess', cardTypeData)
    } catch (e) {
      Logger.error('Error getting card types')
      Logger.debug(e)

      let serviceCallError

      if (e.response && e.response.data && e.response.data.error && e.response.data.error.length > 0) {
        serviceCallError = e.response.data.error.join('\n\n')
      } else {
        serviceCallError = `${Language.$t('global.get_card_types_query_error')}: ${e.message}`
      }

      throw new Error(serviceCallError)
    }
  },

  async getPayDebitCards (context) {
    try {
      const paymentProcessor = context.rootGetters['loans/getLoanPaymentProcessorType']
      const response = await api.paymentMethod.getDebitCards(paymentProcessor)

      const tempCards = response.cards
      const validations = response.validations

      if (validations !== undefined) {
        context.commit('getPayDebitCardsSuccess', { validations })
      }

      if (isEmpty(tempCards)) {
        context.commit('getPayDebitCardsSuccess', { cards: [] })
        return
      }

      let cards = CreditCard.formatCardsByType(context.state.bridgePayCardTypes, tempCards)
      cards = CreditCard.sortCards(cards)

      Logger.info('Formatted cards')
      Logger.debug(cards)

      context.commit('getPayDebitCardsSuccess', { cards })
    } catch (e) {
      throw new Error(`${Language.$t('global.get_cards_query_error')}: ${e.message}`)
    }
  },

  async addPayDebitCard (context, cardData) {
    try {
      const isGuestPayment = context.rootState.payment.guestPayment || false
      const saveAsGuestCard = !isEmpty(cardData.card.savePaymentMethod) ? !cardData.card.savePaymentMethod : false
      const isGuestCard = isGuestPayment || saveAsGuestCard

      const tokenPayTempToken = context.state.bridgePayToken
      if (isEmpty(tokenPayTempToken)) {
        throw new Error('Please confirm card information is entered correctly') // Open ended message, since we do not know specifically where BridgePay fails
      }

      const card = await api.paymentMethod.addDebitCard(
        isGuestCard,
        tokenPayTempToken
      )

      context.commit('setTempBridgePayToken', undefined)

      const cardObj = {
        cardIds: card.card.cardIds,
        cardNumber: `•••• •••• •••• ${card.card.cardNumber}`,
        cardType: card.card.cardType,
        cardholderName: cardData.card.name,
        customerId: context.rootState.customer.customerId,
        customerIds: card.card.customerIds,
        expirationMonth: card.card.expirationMonth,
        expirationYear: card.card.expirationYear,
        guestCard: card.card.guestCard,
        newCard: true
      }

      const formattedCard = CreditCard.formatCardByCardType(context.state.bridgePayCardTypes, cardObj)

      context.commit('addPayDebitCardSuccess', formattedCard)

      if (cardData.saveToPaymentStore === true) {
        context.commit('payment/setPaymentData', {
          paymentCardIds: card.card.cardIds,
          cardHolderName: cardData.card.name,
          cardNumber: card.card.cardNumber,
          cardType: formattedCard.cardType,
          guestCard: card.card.guestCard,
          saveGuestCard: isGuestPayment && !saveAsGuestCard, // Currently only saving guest cards after payment is completed in a guest payment flow
          expirationMonth: formattedCard.expirationMonth,
          expirationYear: formattedCard.expirationYear
        }, { root: true })
      }

      return cardObj
    } catch (e) {
      let serviceCallError

      if (e.message === 'InternalErrorException') {
        serviceCallError = Language.$t('global.server_error_retry')
      } else if (e.message === 'TooManyRequestsException' || e.message === 'LimitExceededException') {
        serviceCallError = Language.$t('global.add_card_too_many_requests_error')
      } else if (e.message === 'AccessDeniedException') {
        serviceCallError = Language.$t('global.add_card_access_denied_error')
      } else if (e.message === 'ServiceUnavailableException') {
        serviceCallError = Language.$t('global.add_card_service_unavailable_error')
      } else if (e.message === 'RequestTimeoutException') {
        serviceCallError = Language.$t('global.add_card_request_timeout_error')
      } else if (e.response && e.response.data && e.response.data.error) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async addDisbursementDebitCard (context, data) {
    try {
      const isOrigination = data.isOrigination
      const isGuestPayment = context.rootState.payment.guestPayment || false
      const saveAsGuestCard = !isEmpty(data.card.savePaymentMethod) ? !data.card.savePaymentMethod : false
      const isGuestCard = isOrigination ? false : (isGuestPayment || saveAsGuestCard)
      const productId = isOrigination ? context.rootState.origination.productId : context.rootState.payment.loan.productId

      const tokenPayTempToken = context.state.bridgePayToken
      if (isEmpty(tokenPayTempToken)) {
        throw new Error('Please confirm card information is entered correctly') // Open ended message, since we do not know specifically where BridgePay fails
      }

      const card = await api.disburseMethod.addDisburseCard(
        isGuestCard,
        tokenPayTempToken,
        productId
      )

      context.commit('setTempBridgePayToken', undefined)

      if (isOrigination) {
        const cardObj = {
          cardId: card.card.id,
          cardNumber: `•••• •••• •••• ${card.card.cardNumber}`,
          cardType: card.card.cardType,
          cardHolderName: card.card.cardholderName
        }

        context.commit('payment/setDisburseCard', cardObj, { root: true })
      }
    } catch (e) {
      let serviceCallError

      if (e.message === 'InternalErrorException') {
        serviceCallError = Language.$t('global.server_error_retry')
      } else if (e.message === 'TooManyRequestsException' || e.message === 'LimitExceededException') {
        serviceCallError = Language.$t('global.add_card_too_many_requests_error')
      } else if (e.message === 'AccessDeniedException') {
        serviceCallError = Language.$t('global.add_card_access_denied_error')
      } else if (e.message === 'ServiceUnavailableException') {
        serviceCallError = Language.$t('global.add_card_service_unavailable_error')
      } else if (e.message === 'RequestTimeoutException') {
        serviceCallError = Language.$t('global.add_card_request_timeout_error')
      } else if (e.response && e.response.data && e.response.data.error) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  /**
   * Gets the secure acceptance session data.
   * @returns {Promise<{acceptance: Object, acceptanceConfig: {formUrl: String}}>} The secure acceptance form data.
   */
  async getSecureAcceptData (context) {
    try {
      const result = await context.dispatch('getCardFormKey', {
        transactionType: Cards.transactionType.disbursement.id
      })

      return result
    } catch (e) {
      let serviceCallError

      if (e.response && e.response.data && e.response.data.error && e.response.data.error.length > 0) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async getSecureAcceptStatus (context, data) {
    const transactionUuid = data.transactionUuid

    try {
      const status = await api.disburseMethod.getSecureAcceptStatus(transactionUuid)

      if (isEmpty(status)) {
        Logger.debug('No card status found')
        return false
      }

      context.commit('payment/setDisburseCard', {
        cardId: status.paymentToken,
        cardHolderName: `${status.firstName} ${status.lastName}`,
        cardNumber: status.cardNumber,
        cardType: status.cardType
      }, { root: true })

      return true
    } catch (e) {
      let serviceCallError

      if (e.response && e.response.data && e.response.data.error && e.response.data.error.length > 0) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async removePayDebitCard (context, cardData) {
    try {
      await api.paymentMethod.removeDebitCard(cardData.cardIds)

      context.commit('removePayDebitCardSuccess', { cardIds: cardData.cardIds })
    } catch (e) {
      let serviceCallError

      if (e.response && e.response.data && e.response.data.error) {
        serviceCallError = `${Language.$t('global.remove_card_unknown_error')} ${e.response.data.error}`
      } else if (e.message === 'InternalErrorException') {
        serviceCallError = Language.$t('global.server_error_retry')
      } else if (e.message === 'TooManyRequestsException' || e.message === 'LimitExceededException') {
        serviceCallError = Language.$t('global.remove_card_too_many_requests_error')
      } else if (e.message === 'AccessDeniedException') {
        serviceCallError = Language.$t('global.remove_card_access_denied_error')
      } else if (e.message === 'ServiceUnavailableException') {
        serviceCallError = Language.$t('global.remove_card_service_unavailable_error')
      } else if (e.message === 'RequestTimeoutException') {
        serviceCallError = Language.$t('global.remove_card_request_timeout_error')
      } else {
        serviceCallError = `${Language.$t('global.remove_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async getCardFormKey (context, data) {
    try {
      const origin = window.location.origin
      return api.paymentMethod.getCardFormKey(data.transactionType, origin)
    } catch (e) {
      Logger.error('Error getting card form key')
      Logger.debug(e)
      throw e
    }
  },

  async getDisburseCards (context, isOrigination = false) {
    try {
      const productId = isOrigination ? context.rootState.origination.productId : context.rootGetters['loans/getDisbursementProductId']

      const response = await api.disburseMethod.getCards(productId).catch(e => {
        Logger.warn('Error getting disburse cards, trying again... ')

        return api.disburseMethod.getCards(productId)
      })

      if (!isEmpty(response) && !isEmpty(response.cards)) {
        // Set Default card
        const card = {
          cardId: response.cards[0].cardId,
          cardHolderName: response.cards[0].cardholderName,
          cardNumber: response.cards[0].cardNumber,
          cardType: response.cards[0].cardType
        }
        context.commit('payment/setDisburseCard', card, { root: true })
      }

      context.commit('setDisburseCards', response)
    } catch (e) {
      Logger.error('Error getting disburse cards')
      Logger.debug(e)
      throw e
    }
  },

  async deleteDisburseCard (context, data) {
    try {
      const isOrigination = data.isOrigination
      const cardId = data.cardId
      const productId = isOrigination ? context.rootState.origination.productId : context.rootState.payment.loan.productId // don't need to consider product switches with deletes so just use the current loan

      Logger.info(`Removing card ID ${cardId}. Product ID: ${productId}`)

      await api.disburseMethod.removeCard(cardId, productId)

      if (context.rootState.payment.disburseCardId === cardId) {
        context.commit('payment/clearDisburseCard', undefined, { root: true }) // Clear from payment store is its already been selected
      }

      context.commit('removeDisburseCards', cardId)
    } catch (e) {
      Logger.error('Error getting disburse cards')
      Logger.debug(e)
      throw e
    }
  }
}
